import { useEffect, useState } from "react";
import { SlidersHorizontal, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MultiSelectFilter } from "@/components/MultiSelectFilter";
import { ProductFilterSelect } from "@/components/ProductFilterSelect";
import { CycleFilterSelect } from "@/components/CycleFilterSelect";
import { LabelFilterSelect } from "@/components/LabelFilterSelect";
import { AssigneeFilterSelect } from "@/components/AssigneeFilterSelect";
import { useLeadAssignmentPolicy } from "@/hooks/useLeadAssignment";
import { usePipelineStagesQuery } from "@/hooks/usePipelineStages";
import { useCrmStore } from "@/stores/crm.store";

/**
 * The single filter bar for Pipeline de Prospectos — it drives the board and the
 * table alike, so switching view keeps whatever is selected.
 *
 * Desktop renders every control in one wrapping row. Mobile keeps the search box
 * always visible (the most-used filter) and moves the rest into a bottom sheet
 * behind a button that carries a count of what's active.
 */
export function LeadFilters({
  trailing,
}: {
  /**
   * Rendered after the LAST filter, in BOTH trees.
   *
   * A slot rather than the button itself: this component's contract is
   * "filters", and a money-spending mutation inside it would mean the next
   * filter someone adds has no rule for whether it goes before or after. The
   * slot also keeps the mobile sheet and the desktop row in step by
   * construction — today it is easy to add something to one and forget the
   * other.
   */
  trailing?: React.ReactNode;
} = {}): React.JSX.Element {
  const setFilter = useCrmStore((s) => s.setFilter);
  const reset = useCrmStore((s) => s.reset);
  const stagesSelected = useCrmStore((s) => s.stages);
  const toggleStage = useCrmStore((s) => s.toggleStage);
  const clearStages = useCrmStore((s) => s.clearStages);
  const channel = useCrmStore((s) => s.channel);
  const scoreMin = useCrmStore((s) => s.scoreMin);
  const scoreMax = useCrmStore((s) => s.scoreMax);
  const productIds = useCrmStore((s) => s.productIds);
  const cycleIds = useCrmStore((s) => s.cycleIds);
  const search = useCrmStore((s) => s.search);
  const from = useCrmStore((s) => s.from);
  const to = useCrmStore((s) => s.to);
  const labelIds = useCrmStore((s) => s.labelIds);
  const assigneeIds = useCrmStore((s) => s.assigneeIds);
  const clearAssignees = useCrmStore((s) => s.clearAssignees);
  const { data: assignmentPolicy } = useLeadAssignmentPolicy();

  // The «Asignado a» control disappears when an admin turns assignment off; a
  // selection left behind would keep narrowing the list with nothing on screen
  // to explain it. Only once the policy has LOADED as off — while it loads, a
  // selection made a moment ago must survive.
  const assignmentOff = assignmentPolicy !== undefined && !assignmentPolicy.enabled;
  useEffect(() => {
    if (assignmentOff && assigneeIds.length > 0) clearAssignees();
  }, [assignmentOff, assigneeIds.length, clearAssignees]);

  // Stage options come from the client's own pipeline columns. They used to be a
  // hardcoded list, which left every custom column unfilterable.
  const { data: stages } = usePipelineStagesQuery();

  const [searchInput, setSearchInput] = useState(search);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Everything except search — what the mobile sheet holds, and what its badge counts.
  const activeCount =
    [channel, scoreMin, scoreMax, from, to].filter(Boolean).length +
    stagesSelected.length +
    productIds.length +
    cycleIds.length +
    labelIds.length +
    assigneeIds.length;
  const hasActiveFilters = activeCount > 0 || !!searchInput;

  function handleClear(): void {
    setSearchInput("");
    reset();
    setSheetOpen(false);
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilter("search", searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, setFilter]);

  const searchBox = (
    <Input
      placeholder="Buscar por nombre, teléfono o ref…"
      value={searchInput}
      onChange={(e) => setSearchInput(e.target.value)}
    />
  );

  const stageSelect = (
    <MultiSelectFilter
      options={(stages ?? []).map((s) => ({ id: s.key, name: s.label }))}
      selected={stagesSelected}
      onToggle={toggleStage}
      onClear={clearStages}
      placeholder="Todas las etapas"
      pluralNoun="etapas"
      singularNoun="etapa"
      clearLabel="Todas las etapas"
      ariaLabel="Etapa"
      className="md:w-[160px]"
    />
  );

  const channelSelect = (
    <Select
      value={channel || "all"}
      onValueChange={(v) => setFilter("channel", v === "all" ? "" : v)}
    >
      <SelectTrigger className="w-full md:w-[130px]">
        <SelectValue placeholder="Canal" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">Todos los canales</SelectItem>
        <SelectItem value="web">Web</SelectItem>
        <SelectItem value="whatsapp">WhatsApp</SelectItem>
      </SelectContent>
    </Select>
  );

  const scoreRange = (
    <div className="flex items-center gap-1">
      <Input
        type="number"
        placeholder="Score mín"
        min={0}
        max={100}
        value={scoreMin}
        onChange={(e) => setFilter("scoreMin", e.target.value)}
        className="w-full md:w-[90px]"
      />
      <span className="text-xs text-ink-300">–</span>
      <Input
        type="number"
        placeholder="Score máx"
        min={0}
        max={100}
        value={scoreMax}
        onChange={(e) => setFilter("scoreMax", e.target.value)}
        className="w-full md:w-[90px]"
      />
    </div>
  );

  const dateRange = (
    <div className="flex items-center gap-1">
      <Input
        type="date"
        value={from}
        onChange={(e) => setFilter("from", e.target.value)}
        className="w-full md:w-[140px]"
      />
      <span className="text-xs text-ink-300">–</span>
      <Input
        type="date"
        value={to}
        onChange={(e) => setFilter("to", e.target.value)}
        className="w-full md:w-[140px]"
      />
    </div>
  );

  return (
    <>
      {/* ── Mobile: search + filter sheet ─────────────── */}
      <div className="md:hidden">
        <div className="flex items-center gap-2 px-4 py-2.5 bg-white border-b border-ink-100">
          <div className="flex-1">{searchBox}</div>
          <button
            type="button"
            onClick={() => setSheetOpen(true)}
            className={`flex items-center gap-1.5 rounded-md border px-3 py-2 text-[12px] font-semibold cursor-pointer ${
              activeCount > 0
                ? "border-moss bg-lime-pale text-moss"
                : "border-ink-200 text-ink-700"
            }`}
          >
            <SlidersHorizontal size={14} />
            Filtros
            {activeCount > 0 && (
              <span className="rounded-full bg-moss px-1.5 text-[10px] font-bold text-white">
                {activeCount}
              </span>
            )}
          </button>
        </div>

        {sheetOpen && (
          <div className="fixed inset-0 z-50 flex flex-col justify-end">
            <button
              type="button"
              aria-label="Cerrar filtros"
              onClick={() => setSheetOpen(false)}
              className="absolute inset-0 bg-black/30"
            />
            <div className="relative animate-sheet-up rounded-t-2xl bg-white p-4 pb-8">
              <div className="mb-3 flex items-center justify-between">
                <span className="font-display text-[18px] font-bold text-ink-900">
                  Filtros
                </span>
                <button
                  type="button"
                  onClick={() => setSheetOpen(false)}
                  className="p-1 text-ink-400 cursor-pointer"
                  aria-label="Cerrar"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="flex flex-col gap-3">
                {stageSelect}
                {channelSelect}
                <ProductFilterSelect />
                <CycleFilterSelect />
                <LabelFilterSelect />
                <AssigneeFilterSelect />
                {scoreRange}
                {dateRange}
                {trailing}
              </div>
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  onClick={handleClear}
                  className="mt-3 w-full text-ink-500"
                >
                  Limpiar filtros
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Desktop: one wrapping row ─────────────────── */}
      <div className="hidden md:flex flex-col gap-2">
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[180px] flex-1">{searchBox}</div>
          {stageSelect}
          {channelSelect}
          <ProductFilterSelect />
          <CycleFilterSelect />
          <LabelFilterSelect />
          <AssigneeFilterSelect />
          {scoreRange}
          {dateRange}
          {trailing}
        </div>

        {hasActiveFilters && (
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="text-ink-400 hover:text-ink-700"
            >
              Limpiar filtros
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
